// src/components/Chat/MessageReactions.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import './MessageReactions.css';

// Normalize a single "user-like" value into { id, name }
const toUser = (rawU) => {
    if (!rawU) return null;
    // If already an object, use common properties
    const id = rawU._id || rawU.id || rawU.userId || (typeof rawU === 'string' ? rawU : null);
    if (!id) return null;

    // Prioritize standard names
    const name = rawU.username || rawU.name || id;

    return { id: id.toString(), name }; // Ensure ID is always a string for consistent Map keys
};

// Build grouped reactions in a robust way
const groupReactions = (input) => {
    const reactions = Array.isArray(input) ? input : [];
    // Map: emoji -> Map(userId -> {id,name}) to handle de-duplication
    const grouped = new Map();

    for (const r of reactions) {
        if (!r) continue;
        const emoji = r.emoji || r.type || r.key;
        if (!emoji) continue;

        // Consolidate all users from the current reaction object
        const usersArr = Array.isArray(r.users)
            ? r.users
            : (r.user ? [r.user] : []);

        // Initialize the inner Map for this emoji if it doesn't exist
        if (!grouped.has(emoji)) {
            grouped.set(emoji, new Map());
        }
        const userMap = grouped.get(emoji);

        for (const rawU of usersArr) {
            const u = toUser(rawU);
            if (u) {
                // This ensures de-duplication across all input objects with the same emoji
                userMap.set(u.id, u);
            }
        }
    }

    // Convert to sorted array [{emoji, users:[...]}, ...] by descending count
    return Array.from(grouped.entries())
        .map(([emoji, usersMap]) => ({ emoji, users: Array.from(usersMap.values()) }))
        .sort((a, b) => b.users.length - a.users.length);
};

const MessageReactions = ({ reactions = [], currentUserId, onReact, onShowMore }) => {
    const grouped = useMemo(() => groupReactions(reactions), [reactions]);
    const topReactions = grouped.slice(0, 3);
    const hasMore = grouped.length > 3;

    // Nothing to render if no reactions at all
    if (topReactions.length === 0) return null;

    return (
        <div className="message-reactions" role="group" aria-label="Message reactions">
            {topReactions.map(({ emoji, users }) => {
                const hasReacted = users.some(u => u.id === currentUserId);
                const tooltip = users.map(u => u.name).join(', ');
                const count = users.length;

                return (
                    <button
                        key={emoji}
                        className={`reaction-button ${hasReacted ? 'reacted' : ''}`}
                        onClick={() => onReact?.(emoji)}
                        title={tooltip}
                        aria-label={`Reacted with ${emoji}. ${count} user${count > 1 ? 's' : ''}`}
                        aria-pressed={hasReacted}
                        type="button"
                    >
                        {emoji}
                        <span className="reaction-count">{count}</span>
                    </button>
                );
            })}

            {hasMore && (
                <button
                    className="reaction-more"
                    title="See all reactions"
                    onClick={onShowMore}
                    aria-label="See all reactions"
                    type="button"
                >
                    +
                </button>
            )}
        </div>
    );
};

MessageReactions.propTypes = {
    reactions: PropTypes.arrayOf(
        PropTypes.oneOfType([
            // Single-user reaction object
            PropTypes.shape({
                emoji: PropTypes.string,
                user: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.shape({
                        _id: PropTypes.string,
                        id: PropTypes.string,
                        userId: PropTypes.string,
                        username: PropTypes.string,
                        name: PropTypes.string,
                    }),
                ]),
            }),
            // Multi-user reaction object
            PropTypes.shape({
                emoji: PropTypes.string,
                users: PropTypes.arrayOf(
                    PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.shape({
                            _id: PropTypes.string,
                            id: PropTypes.string,
                            userId: PropTypes.string,
                            username: PropTypes.string,
                            name: PropTypes.string,
                        }),
                    ])
                ),
            }),
        ])
    ),
    currentUserId: PropTypes.string.isRequired,
    onReact: PropTypes.func.isRequired,
    onShowMore: PropTypes.func,
};

export default MessageReactions;